import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import FieldRenderer from "@/components/FieldRenderer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Inbox } from "lucide-react";
import AiAssistant from "@/components/AiAssistant";

/**
 * Generic list collection manager.
 * endpoint: base like "experiences". fields: field configs.
 * summary(item) -> {title, subtitle, meta}. addLabel: button text.
 */
export default function ListSection({ endpoint, fields, summary, addLabel, emptyText, extraAction, ai }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/${endpoint}`).then((res) => setItems(res.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => { setEditing(null); setValues({}); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setValues(item); setOpen(true); };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, { data: values });
      else await api.post(`/${endpoint}`, { data: values });
      toast.success("Sparat");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/${endpoint}/${id}`);
    toast.success("Borttaget");
    load();
  };

  if (items === null) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div className="stagger-in">
      <div className="mb-5 flex justify-end gap-2">
        {ai && (
          <AiAssistant
            triggerLabel="Fråga AI om hjälp"
            triggerTestId={`${endpoint}-ai-trigger`}
            title={ai.title}
            intro={ai.intro}
            suggestions={ai.suggestions || []}
            loadHistory={async () => (await api.get(`/assistant/profile/${ai.section}`)).data}
            send={async (message) => (await api.post("/assistant/profile", { section: ai.section, message })).data}
            onApply={async (p) => {
              await api.post(`/${endpoint}`, { data: ai.mapProposal(p) });
              toast.success("Tillagt i din profil");
              await load();
            }}
          />
        )}
        <Button data-testid="add-item-btn" className="rounded-full" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1.5" /> {addLabel}
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-2 border-border p-12 text-center">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{emptyText || "Inget tillagt ännu."}</p>
          <Button variant="outline" className="rounded-full mt-4" onClick={openNew}>
            <Plus className="h-4 w-4 mr-1.5" /> {addLabel}
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => {
            const s = summary(item);
            return (
              <Card key={item.id} data-testid={`item-${item.id}`} className="rounded-2xl border-border p-5 flex items-start justify-between gap-4 transition-transform hover:-translate-y-0.5">
                <div className="min-w-0">
                  <div className="font-head font-semibold text-lg truncate">{s.title || "—"}
                    {item.source === "uploaded_cv" && <Badge variant="secondary" className="rounded-full ml-2 text-xs align-middle">Uppladdat CV</Badge>}
                  </div>
                  {s.subtitle && <div className="text-sm text-primary">{s.subtitle}</div>}
                  {s.meta && <div className="text-sm text-muted-foreground mt-0.5">{s.meta}</div>}
                  {s.body && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{s.body}</p>}
                  {extraAction && <div className="mt-3">{extraAction(item, load)}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button data-testid={`edit-${item.id}`} variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-${item.id}`} variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Ta bort?</AlertDialogTitle>
                        <AlertDialogDescription>Detta tas bort från din Masterprofil. Åtgärden kan inte ångras.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-${item.id}`} onClick={() => remove(item.id)}>Ta bort</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Redigera" : addLabel}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "md:col-span-2" : ""}>
                <FieldRenderer field={f} value={values[f.name]} onChange={(v) => setValues((p) => ({ ...p, [f.name]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button data-testid="save-item-btn" className="rounded-full" onClick={submit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
